import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip";
import type { ConsoleEntry } from "../types/consoleTypes";

type TestPlanHeaderProps = {
  entry: ConsoleEntry;
  result?: boolean | 'pass' | 'fail' | 'timeout' | string;
  hasFailedEntry?: boolean;
  hasPassedEntry?: boolean;
};

/**
 * Renders the sticky header row for a test-plan group inside the console table.
 * Shows the test plan name on the left and Pass/Fail/Timeout outcome on the right.
 * Only visible in validate deployment mode.
 */
const TestPlanHeader = ({ entry, result, hasFailedEntry = false, hasPassedEntry = false }: TestPlanHeaderProps) => {
  const testCaseLabel = entry?.testCaseName ?? entry?.testCaseResultId ?? entry?.testCaseId ?? "—";
  const rawFailureHandling =
    entry?.failureHandlingMode ??
    (entry?.CommandDetails as any)?.failureHandlingMode;

  const isSkipAndContinue = rawFailureHandling === "SKIP_AND_CONTINUE";
  const isAbortOnFailure =
    rawFailureHandling &&
    (String(rawFailureHandling).toUpperCase() === "ABORT_ON_FAILURE" ||
      String(rawFailureHandling).toUpperCase() === "ABORT");

  const failureHandlingLabel = rawFailureHandling
    ? String(rawFailureHandling).replace(/_/g, " ").toUpperCase()
    : null;

  const displayLabel = isSkipAndContinue ? "PARTIALLY EXECUTED" : failureHandlingLabel;
  const showBadge = Boolean(displayLabel && hasPassedEntry && hasFailedEntry);

  const customStyle = isSkipAndContinue
    ? {
        background: "rgba(245, 158, 11, 0.15)",
        border: "0.5px solid rgba(245, 158, 11, 0.35)",
        color: "#fcd34d",
      }
    : isAbortOnFailure
    ? {
        background: "rgba(255, 51, 51, 0.15)",
        border: "0.5px solid rgba(255, 51, 51, 0.35)",
        color: "#fca5a5",
      }
    : undefined;

  return (
    <div className="end-of-row-section">
      <span className="tph-name-row">
        {entry?.testPlanName ? (
          <>
            <span className="test-plan-text">{entry.testPlanName}</span>
            <span className="test-case-text">|</span>
            <span className="test-case-text">{testCaseLabel}</span>
          </>
        ) : (
          <span className="test-case-text">{testCaseLabel}</span>
        )}
        {showBadge && displayLabel && (
          <span className="tph-failure-handling-badge" style={customStyle}>
            {displayLabel}
          </span>
        )}
      </span>
      <div className="testcase-button-container">
        {result !== undefined && (
          <span
            className={`tph-result tph-result--${
              result === "pass" || result === true
                ? "pass"
                : result === "timeout"
                ? "timeout"
                : "fail"
            }`}
          >
            {result === "pass" || result === true ? (
              "Pass"
            ) : result === "timeout" ? (
              "Timeout"
            ) : (
              "Fail"
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default TestPlanHeader;
